import { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom/client";
import html2canvas from "html2canvas";

/* ── Shared elements ── */

function Brand({ light, size = 11 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <svg width={size * 1.4} height={size * 1.4} viewBox="0 0 48 48" fill="none">
        <path d="M18 4L20.5 14L31 16.5L20.5 19L18 29L15.5 19L5 16.5L15.5 14Z" fill={light ? "#fff" : "#4B6AE8"}/>
        <path d="M34 6L35.2 10L39 11.2L35.2 12.4L34 16L32.8 12.4L29 11.2L32.8 10Z" fill={light ? "rgba(255,255,255,0.7)" : "#4B6AE8"} opacity="0.7"/>
      </svg>
      <span style={{ fontSize: size, fontWeight: 700, color: light ? "#fff" : "#0f172a", fontFamily: "'Inter',sans-serif" }}>
        Magic<span style={{ fontWeight: 400 }}>Feed</span>
        <span style={{ color: light ? "#c7d2fe" : "#4B6AE8", fontSize: size * 0.78, fontWeight: 700, marginLeft: 1 }}>Pro</span>
      </span>
    </div>
  );
}

function Gauge({ score, size = 80, bg = "rgba(255,255,255,0.08)" }) {
  const sw = Math.max(4, size * 0.07);
  const r = (size - sw * 2) / 2;
  const circ = 2 * Math.PI * r;
  const color = score < 40 ? "#EF4444" : score < 70 ? "#F59E0B" : "#10B981";
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={bg} strokeWidth={sw}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={sw}
        strokeDasharray={`${(score / 100) * circ} ${circ}`} strokeLinecap="round"
        style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%" }}/>
      <text x={size/2} y={size/2 - 1} textAnchor="middle" dominantBaseline="central"
        fill={color} fontSize={size * 0.24} fontWeight="800" fontFamily="Inter,sans-serif">{score}</text>
      <text x={size/2} y={size/2 + size * 0.14} textAnchor="middle"
        fill="#94a3b8" fontSize={size * 0.09} fontFamily="Inter,sans-serif">/100</text>
    </svg>
  );
}

/* Animated gauge: counts from startScore to endScore */
function AnimatedGauge({ startScore = 32, endScore = 98, size = 80, bg, cycleDuration = 6000 }) {
  const [score, setScore] = useState(startScore);
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        setScore(startScore);
        await new Promise(r => setTimeout(r, 1000));
        const steps = endScore - startScore;
        for (let i = 0; i <= steps; i++) {
          if (!running) return;
          setScore(startScore + i);
          await new Promise(r => setTimeout(r, 18));
        }
        await new Promise(r => setTimeout(r, cycleDuration - 1000 - steps * 18));
      }
    };
    loop();
    return () => { running = false; };
  }, [startScore, endScore, cycleDuration]);
  return <Gauge score={score} size={size} bg={bg} />;
}

/* Animated bar chart */
function AnimatedBars({ isW }) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        setProgress(0);
        await new Promise(r => setTimeout(r, 800));
        for (let i = 0; i <= 100; i += 2) {
          if (!running) return;
          setProgress(i);
          await new Promise(r => setTimeout(r, 20));
        }
        await new Promise(r => setTimeout(r, 3000));
      }
    };
    loop();
    return () => { running = false; };
  }, []);
  const h1 = isW ? 32 : 28;
  const h2 = isW ? 72 : 60;
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 10, height: isW ? 80 : 68, flexShrink: 0 }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 30, height: h1, background: "rgba(255,255,255,0.12)", borderRadius: "5px 5px 0 0" }}/>
        <div style={{ fontSize: 7, color: "#818cf8", marginTop: 3 }}>Before</div>
      </div>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 30, height: h2 * (progress / 100), background: "linear-gradient(180deg, #818cf8, #4B6AE8)", borderRadius: "5px 5px 0 0", boxShadow: "0 0 12px rgba(129,140,248,0.3)", transition: "height 0.05s linear" }}/>
        <div style={{ fontSize: 7, color: "#a5b4fc", marginTop: 3 }}>After</div>
      </div>
    </div>
  );
}

/* Animated typing text */
function TypingText({ before, after, cycleDuration = 7000 }) {
  const [text, setText] = useState(before);
  const [phase, setPhase] = useState("before"); // before | typing | done
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        setText(before); setPhase("before");
        await new Promise(r => setTimeout(r, 1500));
        if (!running) return;
        setPhase("typing");
        for (let i = 0; i <= after.length; i++) {
          if (!running) return;
          setText(after.slice(0, i));
          await new Promise(r => setTimeout(r, 30));
        }
        setPhase("done");
        await new Promise(r => setTimeout(r, cycleDuration - 1500 - after.length * 30));
      }
    };
    loop();
    return () => { running = false; };
  }, [before, after, cycleDuration]);
  return { text, phase };
}

/* Cascade counter animation */
function CascadeCounters({ isW }) {
  const [visible, setVisible] = useState(0);
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        setVisible(0);
        await new Promise(r => setTimeout(r, 600));
        for (let i = 1; i <= 3; i++) {
          if (!running) return;
          setVisible(i);
          await new Promise(r => setTimeout(r, 700));
        }
        await new Promise(r => setTimeout(r, 4000));
      }
    };
    loop();
    return () => { running = false; };
  }, []);
  const items = [
    { val: "+67%", label: "Impressions", color: "#fff" },
    { val: "+43%", label: "Clicks", color: "#fff" },
    { val: "+38%", label: "Revenue", color: "#10B981" },
  ];
  return (
    <div style={{ display: "flex", flexDirection: isW ? "row" : "column", gap: 6 }}>
      {items.map((s, i) => (
        <div key={i} style={{
          display: "flex", alignItems: "center", gap: 8,
          background: "rgba(255,255,255,0.08)", borderRadius: 6, padding: isW ? "6px 10px" : "5px 10px",
          opacity: i < visible ? 1 : 0.15, transform: i < visible ? "translateY(0)" : "translateY(6px)",
          transition: "all 0.5s ease",
        }}>
          <span style={{ fontSize: 9, color: "#a7f3d0", minWidth: isW ? 60 : 55 }}>{s.label}</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: s.color }}>{s.val}</span>
        </div>
      ))}
    </div>
  );
}

/* Highlights check animation */
function HighlightsCheck() {
  const items = ["Material", "Size", "Fit", "Care", "Color"];
  const [checked, setChecked] = useState(0);
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        setChecked(0);
        await new Promise(r => setTimeout(r, 800));
        for (let i = 1; i <= items.length; i++) {
          if (!running) return;
          setChecked(i);
          await new Promise(r => setTimeout(r, 500));
        }
        await new Promise(r => setTimeout(r, 3000));
      }
    };
    loop();
    return () => { running = false; };
  }, [items.length]);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {items.map((item, i) => (
        <div key={i} style={{
          display: "flex", alignItems: "center", gap: 6, fontSize: 10,
          opacity: i < checked ? 1 : 0.3, transition: "opacity 0.3s ease",
        }}>
          <div style={{
            width: 14, height: 14, borderRadius: 4,
            background: i < checked ? "#10B981" : "#e5e7eb",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "background 0.3s ease",
          }}>
            {i < checked && <span style={{ color: "#fff", fontSize: 8, fontWeight: 800 }}>✓</span>}
          </div>
          <span style={{ color: "#374151", fontWeight: 600 }}>{item}</span>
        </div>
      ))}
    </div>
  );
}

function CTA({ text = "Start Free Audit", small }) {
  return (
    <div style={{
      display: "inline-block",
      background: "linear-gradient(135deg, #4B6AE8, #6B8BF5)",
      color: "#fff", fontSize: small ? 10 : 12, fontWeight: 700,
      padding: small ? "5px 14px" : "8px 18px",
      borderRadius: 8, fontFamily: "'Inter',sans-serif",
      boxShadow: "0 3px 12px rgba(75,106,232,0.35)",
    }}>
      {text}
    </div>
  );
}

function Pill({ text, color, bg }) {
  return (
    <span style={{
      fontSize: 9, fontWeight: 600, color, background: bg,
      padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap", display: "inline-block",
    }}>{text}</span>
  );
}

/* Full-bleed base wrapper */
function AdBase({ bg, children, style = {} }) {
  return (
    <div style={{
      width: "100%", height: "100%", position: "relative", overflow: "hidden",
      background: bg, fontFamily: "'Inter',sans-serif",
      display: "flex", flexDirection: "column", ...style,
    }}>
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   10 CREATIVE CONCEPTS — Focus: Shopping Feed Audit
   ══════════════════════════════════════════════════ */

const ADS = [

  /* ── 1. Score Transformation (animated gauge) ── */
  {
    name: "Feed Score Audit",
    desc: "Animated gauge 32→98",
    render: (isW) => (
      <AdBase bg="linear-gradient(135deg, #0f172a 0%, #1a2744 100%)">
        <div style={{ position: "absolute", width: "50%", height: "50%", borderRadius: "50%", background: "radial-gradient(circle, rgba(75,106,232,0.12), transparent)", top: "-5%", right: "-5%", pointerEvents: "none" }}/>
        <div style={{ flex: 1, display: "flex", flexDirection: isW ? "row" : "column", alignItems: "center", justifyContent: "center", padding: isW ? "5% 6%" : "6%", gap: isW ? "5%" : "4%" }}>
          <div style={{ position: "relative", flexShrink: 0 }}>
            <AnimatedGauge startScore={32} endScore={98} size={isW ? 85 : 100}/>
            <div style={{ position: "absolute", top: -4, right: -4, background: "#EF4444", color: "#fff", fontSize: 8, fontWeight: 800, padding: "1px 6px", borderRadius: 8, textDecoration: "line-through", opacity: 0.8 }}>32</div>
          </div>
          <div style={{ textAlign: isW ? "left" : "center", zIndex: 1 }}>
            <div style={{ color: "#EF4444", fontSize: 9, fontWeight: 600, marginBottom: 4, opacity: 0.8 }}>Current score: 32/100</div>
            <div style={{ color: "#fff", fontSize: isW ? 17 : 18, fontWeight: 800, lineHeight: 1.2, marginBottom: 6 }}>
              Audit your<br/>Shopping feed <span style={{ color: "#10B981" }}>now</span>
            </div>
            <div style={{ color: "#94a3b8", fontSize: 9, marginBottom: 10 }}>Free AI audit · 30 seconds</div>
            <Brand light/>
          </div>
        </div>
      </AdBase>
    ),
  },

  /* ── 2. Before / After Title (with typing animation) ── */
  {
    name: "Title Optimization",
    desc: "Before/After with typing",
    render: (isW) => {
      const Typing = () => {
        const { text, phase } = TypingText({
          before: "Laptop bag",
          after: 'Premium Luxury Laptop Backpack — 15.6"',
        });
        return (
          <AdBase bg="#fff">
            <div style={{ flex: 1, display: "flex", flexDirection: isW ? "row" : "column" }}>
              {/* Before */}
              <div style={{ flex: 1, background: "linear-gradient(135deg, #fef2f2, #fee2e2)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: isW ? "4% 5%" : "5% 6%" }}>
                <Pill text="BEFORE" color="#EF4444" bg="rgba(239,68,68,0.1)"/>
                <div style={{ marginTop: 8, background: "#fff", borderRadius: 8, padding: "8px 10px", border: "1.5px solid #fecaca", width: "90%", maxWidth: 170 }}>
                  <div style={{ width: "100%", height: 36, background: "#f9fafb", borderRadius: 5, marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: 18 }}>🎒</span>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#0f172a" }}>Laptop bag</div>
                  <div style={{ fontSize: 8, color: "#EF4444", marginTop: 3, fontWeight: 600 }}>✗ Too short, no keywords</div>
                </div>
              </div>
              {/* After */}
              <div style={{ flex: 1, background: "linear-gradient(135deg, #f0fdf4, #dcfce7)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: isW ? "4% 5%" : "5% 6%" }}>
                <Pill text="AFTER ✦ AI" color="#10B981" bg="rgba(16,185,129,0.1)"/>
                <div style={{ marginTop: 8, background: "#fff", borderRadius: 8, padding: "8px 10px", border: "1.5px solid #bbf7d0", width: "90%", maxWidth: 170 }}>
                  <div style={{ width: "100%", height: 36, background: "linear-gradient(135deg,#eef2ff,#dde5ff)", borderRadius: 5, marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: 18 }}>🎒</span>
                  </div>
                  <div style={{ fontSize: 9, fontWeight: 600, color: "#0f172a", lineHeight: 1.3, minHeight: 24 }}>
                    {phase === "before" ? <span style={{ color: "#94a3b8" }}>...</span> : text}
                    {phase === "typing" && <span style={{ borderRight: "2px solid #4B6AE8", animation: "pulseMF 0.8s infinite" }}/>}
                  </div>
                  {phase === "done" && <div style={{ fontSize: 8, color: "#10B981", marginTop: 2, fontWeight: 600 }}>✓ SEO-optimized</div>}
                </div>
              </div>
            </div>
            <div style={{ padding: "6px 12px", borderTop: "1px solid #f1f1f1", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Brand/>
              <span style={{ fontSize: 8, color: "#6b7280" }}>AI-optimized titles = more clicks</span>
            </div>
          </AdBase>
        );
      };
      return <Typing />;
    },
  },

  /* ── 3. Triple Audit Pillars ── */
  {
    name: "3 Audit Checks",
    desc: "Titles + Descriptions + Highlights",
    render: (isW) => (
      <AdBase bg="linear-gradient(180deg, #f8faff 0%, #eef2ff 100%)">
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: isW ? "4% 6%" : "6%" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#4B6AE8", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 }}>Shopping Feed Audit</div>
          <div style={{ fontSize: isW ? 16 : 15, fontWeight: 800, color: "#0f172a", textAlign: "center", lineHeight: 1.3, marginBottom: 16 }}>
            We check <span style={{ background: "linear-gradient(135deg,#4B6AE8,#818cf8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>3 critical factors</span>
          </div>
          <div style={{ display: "flex", gap: isW ? 16 : 8, width: "100%", justifyContent: "center", maxWidth: isW ? 420 : 300 }}>
            {[
              { icon: "📝", label: "Titles", sub: "Too short?", color: "#EF4444" },
              { icon: "📄", label: "Descriptions", sub: "Rich enough?", color: "#F59E0B" },
              { icon: "⭐", label: "Highlights", sub: "Missing?", color: "#EF4444" },
            ].map((p, i) => (
              <div key={i} style={{
                flex: "1 1 0", background: "#fff", borderRadius: 10, padding: isW ? "10px 8px" : "10px 6px",
                textAlign: "center", border: "1.5px solid #e5e7eb",
                boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
              }}>
                <div style={{ fontSize: 18, marginBottom: 4 }}>{p.icon}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#0f172a", marginBottom: 2 }}>{p.label}</div>
                <div style={{ fontSize: 8, fontWeight: 600, color: p.color }}>{p.sub}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14 }}><CTA text="Audit My Feed" small/></div>
        </div>
        <div style={{ padding: "0 12px 8px", textAlign: "center" }}><Brand/></div>
      </AdBase>
    ),
  },

  /* ── 4. Revenue Impact (animated cascade) ── */
  {
    name: "Revenue Impact",
    desc: "Animated cascade +67% → +43% → +38%",
    render: (isW) => (
      <AdBase bg="linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%)">
        <div style={{ position: "absolute", width: "60%", height: "60%", borderRadius: "50%", background: "radial-gradient(circle, rgba(16,185,129,0.15), transparent)", bottom: "-10%", left: "-10%", pointerEvents: "none" }}/>
        <div style={{ flex: 1, display: "flex", flexDirection: isW ? "row" : "column", alignItems: "center", justifyContent: "center", padding: isW ? "4% 5%" : "5%", gap: isW ? "5%" : "4%" }}>
          <div style={{ textAlign: isW ? "left" : "center", zIndex: 1 }}>
            <div style={{ fontSize: 9, fontWeight: 600, color: "#6ee7b7", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 }}>After Feed Audit</div>
            <div style={{ fontSize: isW ? 36 : 34, fontWeight: 900, color: "#fff", lineHeight: 1, letterSpacing: -2 }}>+38%</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#a7f3d0", marginTop: 3 }}>Revenue</div>
            <div style={{ fontSize: 10, color: "#6ee7b7", marginTop: 6, opacity: 0.8 }}>Same budget. More sales.</div>
          </div>
          <CascadeCounters isW={isW}/>
        </div>
        <div style={{ padding: "0 12px 8px", textAlign: "center" }}><Brand light/></div>
      </AdBase>
    ),
  },

  /* ── 5. Google Shopping #1 ── */
  {
    name: "Shopping Rank #1",
    desc: "Product ranked first after audit",
    render: (isW) => (
      <AdBase bg="#fff">
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: isW ? "4% 5%" : "5%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 8 }}>
            <svg width="14" height="14" viewBox="0 0 48 48"><path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 019.5 24c0-1.59.28-3.14.77-4.59l-7.98-6.19A23.99 23.99 0 000 24c0 3.77.9 7.35 2.56 10.56l7.97-5.97z"/><path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 5.97C6.51 42.62 14.62 48 24 48z"/></svg>
            <span style={{ fontSize: 11, fontWeight: 500, color: "#5f6368" }}>Shopping</span>
          </div>
          <div style={{ fontSize: isW ? 16 : 15, fontWeight: 800, color: "#0f172a", textAlign: "center", lineHeight: 1.3, marginBottom: 12 }}>
            Get to <span style={{ color: "#4B6AE8" }}>#1</span> on Google Shopping
          </div>
          <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 10 }}>
            <div style={{ width: isW ? 90 : 75, background: "#fff", borderRadius: 8, border: "2px solid #4B6AE8", boxShadow: "0 3px 12px rgba(75,106,232,0.18)", overflow: "hidden" }}>
              <div style={{ height: 38, background: "linear-gradient(135deg,#eef2ff,#dde5ff)", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                <span style={{ fontSize: 16 }}>🎒</span>
                <div style={{ position: "absolute", top: 3, left: 3, background: "linear-gradient(135deg,#4B6AE8,#818cf8)", color: "#fff", fontSize: 7, fontWeight: 800, padding: "1px 5px", borderRadius: 8 }}>#1</div>
              </div>
              <div style={{ padding: 4 }}>
                <span style={{ fontSize: 6, fontWeight: 600, color: "#4B6AE8", background: "rgba(75,106,232,0.08)", padding: "1px 3px", borderRadius: 6 }}>✦ Audited</span>
                <div style={{ fontSize: 7, fontWeight: 600, color: "#0f172a", marginTop: 2, lineHeight: 1.3 }}>Premium Luxury Laptop Backpack</div>
                <div style={{ fontSize: 8, fontWeight: 700, color: "#0f172a", marginTop: 1 }}>$89.99</div>
              </div>
            </div>
            {[1, 2].map(i => (
              <div key={i} style={{ width: isW ? 70 : 58, background: "#fff", borderRadius: 8, border: "1px solid #e8eaed", opacity: 0.45, overflow: "hidden" }}>
                <div style={{ height: 38, background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ width: 16, height: 16, borderRadius: 3, border: "1px solid #d1d5db" }}/>
                </div>
                <div style={{ padding: 4 }}>
                  <div style={{ width: "80%", height: 3, background: "#e5e7eb", borderRadius: 2, marginBottom: 3 }}/>
                  <div style={{ width: "50%", height: 3, background: "#e5e7eb", borderRadius: 2 }}/>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5, background: "linear-gradient(135deg,#ecfdf5,#d1fae5)", border: "1px solid #6ee7b7", borderRadius: 16, padding: "3px 10px", marginBottom: 8 }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#10b981" }}/>
            <span style={{ fontSize: 8, fontWeight: 700, color: "#059669" }}>Feed audited → Product is now #1</span>
          </div>
          <Brand/>
        </div>
      </AdBase>
    ),
  },

  /* ── 6. Free Feed Audit CTA ── */
  {
    name: "Free Feed Audit",
    desc: "Bold CTA with animated gauge",
    render: (isW) => (
      <AdBase bg="linear-gradient(135deg, #4B6AE8 0%, #6B8BF5 50%, #818cf8 100%)">
        <div style={{ position: "absolute", width: "50%", height: "50%", borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,0.08), transparent)", top: "-10%", right: "-10%", pointerEvents: "none" }}/>
        <div style={{ flex: 1, display: "flex", flexDirection: isW ? "row" : "column", alignItems: "center", justifyContent: "center", padding: isW ? "4% 5%" : "5%", gap: isW ? "5%" : "4%" }}>
          <div style={{ flexShrink: 0 }}>
            <div style={{ background: "rgba(255,255,255,0.1)", borderRadius: 12, padding: 10, backdropFilter: "blur(10px)" }}>
              <AnimatedGauge startScore={32} endScore={98} size={isW ? 70 : 80} bg="rgba(255,255,255,0.12)"/>
            </div>
          </div>
          <div style={{ textAlign: isW ? "left" : "center", zIndex: 1 }}>
            <div style={{ display: "inline-block", background: "rgba(255,255,255,0.15)", borderRadius: 16, padding: "3px 10px", marginBottom: 8 }}>
              <span style={{ fontSize: 9, fontWeight: 700, color: "#fff" }}>100% FREE</span>
            </div>
            <div style={{ color: "#fff", fontSize: isW ? 18 : 19, fontWeight: 900, lineHeight: 1.15, marginBottom: 6 }}>
              Shopping Feed<br/>Audit
            </div>
            <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 10, marginBottom: 12, lineHeight: 1.4 }}>
              Know your feed score in 30s.<br/>No signup required.
            </div>
            <div style={{
              display: "inline-block", background: "#fff", color: "#4B6AE8",
              fontSize: 11, fontWeight: 700, padding: "7px 16px", borderRadius: 8,
              boxShadow: "0 3px 12px rgba(0,0,0,0.15)",
            }}>
              → Audit My Feed
            </div>
          </div>
        </div>
        <div style={{ padding: "0 12px 6px", textAlign: "center" }}><Brand light size={10}/></div>
      </AdBase>
    ),
  },

  /* ── 7. Missing Highlights (animated checkmarks) ── */
  {
    name: "Highlights Audit",
    desc: "Animated missing highlights check",
    render: (isW) => (
      <AdBase bg="linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)">
        <div style={{ flex: 1, display: "flex", flexDirection: isW ? "row" : "column", alignItems: "center", justifyContent: "center", padding: isW ? "4% 5%" : "5%", gap: isW ? "5%" : "4%" }}>
          <div style={{ textAlign: isW ? "left" : "center", zIndex: 1, flex: 1 }}>
            <Pill text="⚠ AUDIT ALERT" color="#D97706" bg="rgba(217,119,6,0.1)"/>
            <div style={{ fontSize: isW ? 15 : 14, fontWeight: 800, color: "#0f172a", lineHeight: 1.3, margin: "8px 0 6px" }}>
              <span style={{ color: "#D97706" }}>87%</span> of feeds<br/>miss Product Highlights
            </div>
            <div style={{ fontSize: 9, color: "#78716c", marginBottom: 10, lineHeight: 1.5 }}>
              Google uses highlights to rank products.<br/>Our audit detects missing ones instantly.
            </div>
            <CTA text="Check My Feed" small/>
          </div>
          <div style={{ flexShrink: 0 }}>
            <div style={{ background: "#fff", borderRadius: 10, padding: "10px 12px", border: "1px solid #fed7aa", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
              <div style={{ fontSize: 8, fontWeight: 700, color: "#D97706", marginBottom: 6 }}>Highlights Check</div>
              <HighlightsCheck />
            </div>
          </div>
        </div>
        <div style={{ padding: "0 12px 6px" }}><Brand/></div>
      </AdBase>
    ),
  },

  /* ── 8. Impressions Boost (animated bars) ── */
  {
    name: "Impressions Boost",
    desc: "Animated bar chart +67%",
    render: (isW) => (
      <AdBase bg="linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)">
        <div style={{ position: "absolute", width: "40%", height: "40%", borderRadius: "50%", background: "radial-gradient(circle, rgba(129,140,248,0.15), transparent)", bottom: "10%", left: "5%", pointerEvents: "none" }}/>
        <div style={{ flex: 1, display: "flex", flexDirection: isW ? "row" : "column", alignItems: "center", justifyContent: "center", padding: isW ? "4% 5%" : "5%", gap: isW ? "5%" : "4%" }}>
          <div style={{ textAlign: isW ? "left" : "center", zIndex: 1 }}>
            <div style={{ fontSize: 9, fontWeight: 600, color: "#a5b4fc", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 }}>After Feed Audit</div>
            <div style={{ fontSize: isW ? 38 : 34, fontWeight: 900, color: "#fff", lineHeight: 1, letterSpacing: -2 }}>+67%</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#c7d2fe", marginTop: 3 }}>More Impressions</div>
            <div style={{ fontSize: 9, color: "#818cf8", marginTop: 6, opacity: 0.8, lineHeight: 1.4 }}>Optimized feed = more visibility<br/>in Google Shopping</div>
          </div>
          <AnimatedBars isW={isW}/>
        </div>
        <div style={{ padding: "0 12px 6px", textAlign: "center" }}><Brand light/></div>
      </AdBase>
    ),
  },

  /* ── 9. Social Proof ── */
  {
    name: "Merchant Results",
    desc: "200+ audits, real results",
    render: (isW) => (
      <AdBase bg="linear-gradient(180deg, #fff 0%, #f8faff 100%)">
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: isW ? "4% 6%" : "5%", textAlign: "center" }}>
          <div style={{ display: "flex", gap: 2, marginBottom: 8 }}>
            {[...Array(5)].map((_, i) => <span key={i} style={{ fontSize: 16, color: "#FBBF24" }}>★</span>)}
          </div>
          <div style={{ fontSize: isW ? 36 : 32, fontWeight: 900, color: "#0f172a", lineHeight: 1, letterSpacing: -2 }}>200+</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginTop: 4 }}>Feeds Audited</div>
          <div style={{ width: 30, height: 2, background: "linear-gradient(90deg, transparent, #4B6AE8, transparent)", margin: "10px auto", borderRadius: 2 }}/>
          <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.5, maxWidth: 220 }}>
            &ldquo;After auditing our feed, Shopping revenue grew <span style={{ fontWeight: 700, color: "#10B981" }}>+38%</span> in one month.&rdquo;
          </div>
          <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 4, fontStyle: "italic" }}>— Shopify Plus Merchant</div>
          <div style={{ marginTop: 10 }}><CTA text="Audit My Feed" small/></div>
        </div>
        <div style={{ padding: "0 12px 6px", textAlign: "center" }}><Brand/></div>
      </AdBase>
    ),
  },

  /* ── 10. Problem → Solution ── */
  {
    name: "Problem → Audit Fix",
    desc: "Traffic loss → feed audit fix",
    render: (isW) => (
      <AdBase bg="#fff" style={{ flexDirection: isW ? "row" : "column" }}>
        {/* Problem side */}
        <div style={{
          flex: 1, background: "linear-gradient(135deg, #fef2f2, #fee2e2)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          padding: isW ? "4% 5%" : "5%",
        }}>
          <div style={{ fontSize: isW ? 28 : 26, fontWeight: 900, color: "#EF4444", lineHeight: 1, letterSpacing: -1 }}>30-50%</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#991b1b", marginTop: 4, textAlign: "center" }}>of Shopping traffic</div>
          <div style={{ fontSize: 10, color: "#dc2626", marginTop: 2, fontWeight: 600 }}>LOST</div>
          <div style={{ fontSize: 8, color: "#b91c1c", marginTop: 6, textAlign: "center", opacity: 0.7, lineHeight: 1.4 }}>Unoptimized product feed<br/>= invisible products</div>
        </div>

        {/* Arrow */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: isW ? "0 3px" : "3px 0", background: "#fff", zIndex: 2 }}>
          <div style={{
            width: 26, height: 26, borderRadius: "50%",
            background: "linear-gradient(135deg, #4B6AE8, #818cf8)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 8px rgba(75,106,232,0.3)",
          }}>
            <span style={{ color: "#fff", fontSize: 12, fontWeight: 700, transform: isW ? "rotate(0deg)" : "rotate(90deg)", display: "inline-block" }}>→</span>
          </div>
        </div>

        {/* Solution side */}
        <div style={{
          flex: 1, background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          padding: isW ? "4% 5%" : "5%",
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#10B981", marginBottom: 4 }}>✦ AI Feed Audit</div>
          <div style={{ fontSize: isW ? 16 : 15, fontWeight: 800, color: "#0f172a", textAlign: "center", lineHeight: 1.3 }}>
            Fix it in<br/><span style={{ color: "#10B981" }}>30 seconds</span>
          </div>
          <div style={{ fontSize: 8, color: "#6b7280", marginTop: 6, textAlign: "center", lineHeight: 1.4 }}>Titles, descriptions &<br/>highlights — audited by AI</div>
          <div style={{ marginTop: 8 }}><CTA text="Free Audit" small/></div>
        </div>

        <div style={{ position: "absolute", bottom: 4, left: "50%", transform: "translateX(-50%)" }}><Brand size={9}/></div>
      </AdBase>
    ),
  },

  /* ══════════════════════════════════════════════════
     UI MOCKUP CREATIVES (Square-focused)
     ══════════════════════════════════════════════════ */

  /* ── 11. Google Shopping Search Results ── */
  {
    name: "Shopping Results UI",
    desc: "Google Shopping interface mockup",
    render: (isW) => (
      <AdBase bg="#fff">
        <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: isW ? "12px 16px" : "14px 16px", overflow: "hidden" }}>
          {/* Google header */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 019.5 24c0-1.59.28-3.14.77-4.59l-7.98-6.19A23.99 23.99 0 000 24c0 3.77.9 7.35 2.56 10.56l7.97-5.97z"/><path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 5.97C6.51 42.62 14.62 48 24 48z"/></svg>
            <span style={{ fontSize: 13, fontWeight: 500, color: "#202124" }}>Shopping</span>
          </div>
          {/* Search bar */}
          <div style={{ display: "flex", alignItems: "center", background: "#fff", border: "1px solid #dfe1e5", borderRadius: 24, padding: "8px 14px", marginBottom: 8, boxShadow: "0 1px 6px rgba(32,33,36,0.1)" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#9AA0A6"><path d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
            <span style={{ marginLeft: 8, fontSize: 12, color: "#202124", fontWeight: 400 }}>Luxury Laptop Backpack</span>
            <span style={{ marginLeft: "auto", fontSize: 14, color: "#70757a", cursor: "pointer" }}>×</span>
          </div>
          {/* Tabs */}
          <div style={{ display: "flex", gap: 16, marginBottom: 10, borderBottom: "1px solid #ebebeb", paddingBottom: 6 }}>
            <span style={{ fontSize: 10, color: "#5f6368" }}>All</span>
            <span style={{ fontSize: 10, color: "#1a73e8", fontWeight: 600, borderBottom: "2px solid #1a73e8", paddingBottom: 4 }}>Shopping</span>
            <span style={{ fontSize: 10, color: "#5f6368" }}>Images</span>
            <span style={{ fontSize: 10, color: "#5f6368" }}>News</span>
          </div>
          {/* Sponsored + badge */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: "#202124" }}>Sponsored products</span>
            <div style={{ display: "flex", alignItems: "center", gap: 4, background: "linear-gradient(135deg,#ecfdf5,#d1fae5)", border: "1px solid #6ee7b7", borderRadius: 14, padding: "3px 10px" }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#10b981" }}/>
              <span style={{ fontSize: 8, fontWeight: 700, color: "#059669" }}>Your product is now #1 🎉</span>
            </div>
          </div>
          {/* Product cards row */}
          <div style={{ display: "flex", gap: 8, flex: 1, alignItems: "stretch" }}>
            {/* YOUR product — #1 */}
            <div style={{ flex: "1 1 0", background: "#fff", borderRadius: 10, border: "2px solid #4B6AE8", boxShadow: "0 2px 12px rgba(75,106,232,0.15)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
              <div style={{ flex: 1, minHeight: 0, background: "linear-gradient(135deg,#eef2ff,#dde5ff)", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                <span style={{ fontSize: isW ? 28 : 24 }}>🎒</span>
                <div style={{ position: "absolute", top: 5, left: 5, background: "linear-gradient(135deg,#4B6AE8,#818cf8)", color: "#fff", fontSize: 8, fontWeight: 800, padding: "2px 7px", borderRadius: 10 }}>#1</div>
              </div>
              <div style={{ padding: "6px 8px" }}>
                <span style={{ fontSize: 7, fontWeight: 600, color: "#4B6AE8", background: "rgba(75,106,232,0.08)", padding: "1px 5px", borderRadius: 8 }}>✦ AI Optimized</span>
                <div style={{ fontSize: 8, fontWeight: 600, color: "#0f172a", marginTop: 3, lineHeight: 1.3 }}>Premium & Durable Luxury Laptop Backpack — 15.6"</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>$89.99</div>
              </div>
            </div>
            {/* Ghost competitor cards */}
            {[0, 1, 2].map(i => (
              <div key={i} style={{ flex: "1 1 0", background: "#fff", borderRadius: 10, border: "1px solid #e8eaed", opacity: 0.4, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ flex: 1, minHeight: 0, background: "#f8f9fa", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#dadce0"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
                </div>
                <div style={{ padding: "6px 8px" }}>
                  <div style={{ fontSize: 9, color: "#70757a", fontWeight: 500 }}>Product</div>
                  <div style={{ width: "80%", height: 3, background: "#e8eaed", borderRadius: 2, marginTop: 4 }}/>
                  <div style={{ width: "50%", height: 3, background: "#e8eaed", borderRadius: 2, marginTop: 3 }}/>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Bottom bar */}
        <div style={{ padding: "6px 16px 10px", borderTop: "1px solid #f1f1f1", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Brand/>
          <span style={{ fontSize: 8, color: "#4B6AE8", fontWeight: 600 }}>audit.magicfeedpro.com</span>
        </div>
      </AdBase>
    ),
  },

  /* ── 12. Merchant Center Feed Errors Dashboard ── */
  {
    name: "Feed Errors Dashboard",
    desc: "Merchant Center-style error fixing",
    render: (isW) => {
      const ErrorDashboard = () => {
        const [fixed, setFixed] = useState(0);
        useEffect(() => {
          let running = true;
          const loop = async () => {
            while (running) {
              setFixed(0);
              await new Promise(r => setTimeout(r, 1200));
              for (let i = 1; i <= 4; i++) {
                if (!running) return;
                setFixed(i);
                await new Promise(r => setTimeout(r, 800));
              }
              await new Promise(r => setTimeout(r, 3500));
            }
          };
          loop();
          return () => { running = false; };
        }, []);
        const errors = [
          { label: "Missing product title keywords", count: 847 },
          { label: "Description too short (<500 chars)", count: 612 },
          { label: "No product highlights", count: 1043 },
          { label: "Missing GTIN / brand", count: 234 },
        ];
        return (
          <AdBase bg="#f8f9fa">
            <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: isW ? "12px 16px" : "14px 16px" }}>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 8, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 1 }}>Merchant Center</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a", marginTop: 2 }}>Feed Diagnostics</div>
                </div>
                <div style={{ background: fixed >= 4 ? "#dcfce7" : "#fef3c7", borderRadius: 8, padding: "4px 10px" }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: fixed >= 4 ? "#059669" : "#d97706" }}>
                    {fixed >= 4 ? "✓ All Fixed" : `${4 - fixed} issues`}
                  </span>
                </div>
              </div>
              {/* Error rows */}
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                {errors.map((err, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 8, background: "#fff",
                    borderRadius: 8, padding: "8px 10px",
                    border: `1px solid ${i < fixed ? "#bbf7d0" : "#fecaca"}`,
                    transition: "all 0.5s ease",
                  }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                      background: i < fixed ? "#10B981" : "#EF4444",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "background 0.5s ease",
                    }}>
                      <span style={{ color: "#fff", fontSize: 9, fontWeight: 800 }}>{i < fixed ? "✓" : "!"}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 9, fontWeight: 600, color: "#374151", textDecoration: i < fixed ? "line-through" : "none", opacity: i < fixed ? 0.5 : 1, transition: "all 0.5s ease" }}>{err.label}</div>
                    </div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: i < fixed ? "#10B981" : "#EF4444", flexShrink: 0 }}>
                      {i < fixed ? "Fixed" : `${err.count}`}
                    </div>
                  </div>
                ))}
              </div>
              {/* Progress bar */}
              <div style={{ marginTop: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                  <span style={{ fontSize: 8, color: "#6b7280", fontWeight: 600 }}>Feed Health</span>
                  <span style={{ fontSize: 8, color: "#10B981", fontWeight: 700 }}>{Math.round(fixed / 4 * 100)}%</span>
                </div>
                <div style={{ width: "100%", height: 5, background: "#e5e7eb", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: `${fixed / 4 * 100}%`, height: "100%", background: "linear-gradient(90deg, #10B981, #34d399)", borderRadius: 4, transition: "width 0.8s ease" }}/>
                </div>
              </div>
            </div>
            <div style={{ padding: "6px 16px 10px", borderTop: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Brand/>
              <CTA text="Fix My Feed" small/>
            </div>
          </AdBase>
        );
      };
      return <ErrorDashboard />;
    },
  },

  /* ── 13. Audit Score Report Card ── */
  {
    name: "Audit Report Card",
    desc: "Detailed score breakdown UI",
    render: (isW) => {
      const ReportCard = () => {
        const [reveal, setReveal] = useState(0);
        useEffect(() => {
          let running = true;
          const loop = async () => {
            while (running) {
              setReveal(0);
              await new Promise(r => setTimeout(r, 800));
              for (let i = 1; i <= 4; i++) {
                if (!running) return;
                setReveal(i);
                await new Promise(r => setTimeout(r, 600));
              }
              await new Promise(r => setTimeout(r, 4000));
            }
          };
          loop();
          return () => { running = false; };
        }, []);
        const scores = [
          { label: "Title Quality", score: 18, max: 35, color: "#EF4444", icon: "📝" },
          { label: "Description SEO", score: 8, max: 30, color: "#EF4444", icon: "📄" },
          { label: "Product Highlights", score: 0, max: 20, color: "#EF4444", icon: "⭐" },
          { label: "Attributes", score: 6, max: 15, color: "#F59E0B", icon: "🏷️" },
        ];
        const total = scores.reduce((s, x) => s + x.score, 0);
        return (
          <AdBase bg="#fff">
            <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: isW ? "12px 16px" : "14px 16px" }}>
              {/* Header with overall score */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, paddingBottom: 10, borderBottom: "1px solid #f1f1f1" }}>
                <AnimatedGauge startScore={0} endScore={total} size={60} cycleDuration={8000}/>
                <div>
                  <div style={{ fontSize: 8, fontWeight: 600, color: "#EF4444", textTransform: "uppercase", letterSpacing: 1 }}>Feed Score</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>Your Audit Results</div>
                  <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2 }}>1,247 products analyzed</div>
                </div>
              </div>
              {/* Score breakdown rows */}
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                {scores.map((s, i) => (
                  <div key={i} style={{ opacity: i < reveal ? 1 : 0.15, transform: i < reveal ? "translateX(0)" : "translateX(-8px)", transition: "all 0.4s ease" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 3 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ fontSize: 12 }}>{s.icon}</span>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#374151" }}>{s.label}</span>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, color: s.color }}>{s.score}/{s.max}</span>
                    </div>
                    <div style={{ width: "100%", height: 5, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ width: `${(s.score / s.max) * 100}%`, height: "100%", background: s.color, borderRadius: 4, transition: "width 0.6s ease" }}/>
                    </div>
                  </div>
                ))}
              </div>
              {/* Bottom verdict */}
              <div style={{ marginTop: 10, background: "linear-gradient(135deg, #fef2f2, #fee2e2)", borderRadius: 8, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 16 }}>⚠️</span>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: "#991b1b" }}>Critical: Your feed needs optimization</div>
                  <div style={{ fontSize: 8, color: "#b91c1c" }}>AI can fix all issues automatically</div>
                </div>
              </div>
            </div>
            <div style={{ padding: "6px 16px 10px", borderTop: "1px solid #f1f1f1", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Brand/>
              <CTA text="Get Full Report" small/>
            </div>
          </AdBase>
        );
      };
      return <ReportCard />;
    },
  },

  /* ── 14. Feed Scanner UI ── */
  {
    name: "Feed Scanner",
    desc: "Live feed scanning interface",
    render: (isW) => {
      const Scanner = () => {
        const [progress, setProgress] = useState(0);
        const [phase, setPhase] = useState("scanning"); // scanning | done
        useEffect(() => {
          let running = true;
          const loop = async () => {
            while (running) {
              setPhase("scanning"); setProgress(0);
              for (let i = 0; i <= 100; i++) {
                if (!running) return;
                setProgress(i);
                await new Promise(r => setTimeout(r, 40));
              }
              setPhase("done");
              await new Promise(r => setTimeout(r, 4000));
            }
          };
          loop();
          return () => { running = false; };
        }, []);
        const products = [
          { name: "Laptop Backpack 15.6\"", status: progress > 20 },
          { name: "Wireless Mouse Ergonomic", status: progress > 40 },
          { name: "USB-C Hub 7-in-1", status: progress > 60 },
          { name: "Mechanical Keyboard RGB", status: progress > 80 },
        ];
        return (
          <AdBase bg="linear-gradient(135deg, #0f172a 0%, #1e293b 100%)">
            <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: isW ? "12px 16px" : "14px 16px" }}>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: "linear-gradient(135deg, #4B6AE8, #818cf8)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 14 }}>✦</span>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>Feed Audit Scanner</div>
                  <div style={{ fontSize: 8, color: "#64748b" }}>AI-powered analysis</div>
                </div>
                <div style={{ marginLeft: "auto" }}>
                  {phase === "scanning" && (
                    <div style={{ width: 14, height: 14, border: "2px solid #4B6AE8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
                  )}
                  {phase === "done" && (
                    <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#10B981", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ color: "#fff", fontSize: 10, fontWeight: 800 }}>✓</span>
                    </div>
                  )}
                </div>
              </div>
              {/* Progress bar */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 8, color: "#94a3b8" }}>{phase === "done" ? "Audit complete" : "Scanning products..."}</span>
                  <span style={{ fontSize: 9, color: "#4B6AE8", fontWeight: 700 }}>{progress}%</span>
                </div>
                <div style={{ width: "100%", height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: `${progress}%`, height: "100%", background: "linear-gradient(90deg, #4B6AE8, #818cf8)", borderRadius: 4, transition: "width 0.05s linear", boxShadow: "0 0 10px rgba(75,106,232,0.5)" }}/>
                </div>
              </div>
              {/* Product scan rows */}
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                {products.map((p, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 8,
                    background: "rgba(255,255,255,0.04)", borderRadius: 6, padding: "6px 10px",
                    border: `1px solid ${p.status ? "rgba(75,106,232,0.3)" : "rgba(255,255,255,0.06)"}`,
                    opacity: p.status ? 1 : 0.4, transition: "all 0.5s ease",
                  }}>
                    <div style={{ width: 24, height: 24, borderRadius: 4, background: "rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ fontSize: 10 }}>📦</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 9, fontWeight: 600, color: "#e2e8f0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                    </div>
                    {p.status && (
                      <div style={{ display: "flex", gap: 3 }}>
                        <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#EF4444" }}/>
                        <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#F59E0B" }}/>
                        <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#10B981" }}/>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Score reveal */}
              {phase === "done" && (
                <div style={{ marginTop: 8, background: "rgba(75,106,232,0.12)", borderRadius: 8, padding: "8px 12px", textAlign: "center", border: "1px solid rgba(75,106,232,0.3)" }}>
                  <span style={{ fontSize: 9, color: "#94a3b8" }}>Feed Score: </span>
                  <span style={{ fontSize: 16, fontWeight: 900, color: "#EF4444" }}>32</span>
                  <span style={{ fontSize: 9, color: "#94a3b8" }}>/100 — </span>
                  <span style={{ fontSize: 9, fontWeight: 700, color: "#EF4444" }}>Needs optimization</span>
                </div>
              )}
            </div>
            <div style={{ padding: "6px 16px 10px", borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Brand light/>
              <span style={{ fontSize: 8, color: "#818cf8", fontWeight: 600 }}>Free · No signup</span>
            </div>
          </AdBase>
        );
      };
      return <Scanner />;
    },
  },

  /* ── 15. Product Attributes Checker ── */
  {
    name: "Attributes Checker",
    desc: "Missing vs complete product data",
    render: (isW) => (
      <AdBase bg="#fff">
        <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: isW ? "12px 16px" : "14px 16px" }}>
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 8, fontWeight: 700, color: "#4B6AE8", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 4 }}>Feed Attribute Audit</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>What Google wants vs what you have</div>
          </div>
          {/* Comparison table */}
          <div style={{ flex: 1, display: "flex", gap: isW ? 12 : 8 }}>
            {/* Your feed */}
            <div style={{ flex: 1, background: "linear-gradient(135deg, #fef2f2, #fff)", borderRadius: 10, padding: "10px", border: "1px solid #fecaca" }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#EF4444", textAlign: "center", marginBottom: 8 }}>Your Feed ✗</div>
              {[
                { attr: "Title", val: "Laptop bag", bad: true },
                { attr: "Description", val: "120 chars", bad: true },
                { attr: "Highlights", val: "None", bad: true },
                { attr: "GTIN", val: "Missing", bad: true },
                { attr: "Images", val: "1 photo", bad: true },
              ].map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #fef2f2", alignItems: "center" }}>
                  <span style={{ fontSize: 8, color: "#6b7280", fontWeight: 500 }}>{r.attr}</span>
                  <span style={{ fontSize: 8, color: "#EF4444", fontWeight: 600 }}>{r.val}</span>
                </div>
              ))}
            </div>
            {/* Optimized feed */}
            <div style={{ flex: 1, background: "linear-gradient(135deg, #f0fdf4, #fff)", borderRadius: 10, padding: "10px", border: "1px solid #bbf7d0" }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#10B981", textAlign: "center", marginBottom: 8 }}>After Audit ✓</div>
              {[
                { attr: "Title", val: "150 chars SEO" },
                { attr: "Description", val: "4,970 chars" },
                { attr: "Highlights", val: "5/5 added" },
                { attr: "GTIN", val: "Verified ✓" },
                { attr: "Images", val: "5+ photos" },
              ].map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #f0fdf4", alignItems: "center" }}>
                  <span style={{ fontSize: 8, color: "#6b7280", fontWeight: 500 }}>{r.attr}</span>
                  <span style={{ fontSize: 8, color: "#10B981", fontWeight: 600 }}>{r.val}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Bottom score */}
          <div style={{ marginTop: 10, display: "flex", gap: 8, justifyContent: "center" }}>
            <div style={{ background: "#fef2f2", borderRadius: 8, padding: "6px 14px", textAlign: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: "#EF4444" }}>32</div>
              <div style={{ fontSize: 7, color: "#991b1b", fontWeight: 600 }}>Before</div>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={{ fontSize: 14, color: "#94a3b8" }}>→</span>
            </div>
            <div style={{ background: "#f0fdf4", borderRadius: 8, padding: "6px 14px", textAlign: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: "#10B981" }}>98</div>
              <div style={{ fontSize: 7, color: "#059669", fontWeight: 600 }}>After</div>
            </div>
          </div>
        </div>
        <div style={{ padding: "6px 16px 10px", borderTop: "1px solid #f1f1f1", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Brand/>
          <CTA text="Audit Free" small/>
        </div>
      </AdBase>
    ),
  },

  /* ── 16. Google Ads Performance Dashboard ── */
  {
    name: "Performance Dashboard",
    desc: "Google Ads metrics before/after audit",
    render: (isW) => {
      const Dashboard = () => {
        const [show, setShow] = useState(false);
        useEffect(() => {
          let running = true;
          const loop = async () => {
            while (running) {
              setShow(false);
              await new Promise(r => setTimeout(r, 1500));
              setShow(true);
              await new Promise(r => setTimeout(r, 5000));
            }
          };
          loop();
          return () => { running = false; };
        }, []);
        const metrics = [
          { label: "Impressions", before: "12.4K", after: "20.7K", change: "+67%", color: "#4B6AE8" },
          { label: "Clicks", before: "891", after: "1,274", change: "+43%", color: "#818cf8" },
          { label: "CTR", before: "2.1%", after: "3.8%", change: "+81%", color: "#10B981" },
          { label: "Revenue", before: "$4,230", after: "$5,837", change: "+38%", color: "#10B981" },
        ];
        return (
          <AdBase bg="#fff">
            <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: isW ? "12px 16px" : "14px 16px" }}>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid #f1f1f1" }}>
                <div style={{ width: 24, height: 24, borderRadius: 6, background: "linear-gradient(135deg, #4B6AE8, #818cf8)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ color: "#fff", fontSize: 11, fontWeight: 800 }}>📊</span>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#0f172a" }}>Shopping Campaign</div>
                  <div style={{ fontSize: 8, color: "#94a3b8" }}>30-day performance</div>
                </div>
                <div style={{ marginLeft: "auto", background: show ? "#dcfce7" : "#f1f5f9", borderRadius: 6, padding: "3px 8px", transition: "background 0.5s ease" }}>
                  <span style={{ fontSize: 8, fontWeight: 600, color: show ? "#059669" : "#94a3b8" }}>
                    {show ? "After Audit ✓" : "Before Audit"}
                  </span>
                </div>
              </div>
              {/* Metrics grid */}
              <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {metrics.map((m, i) => (
                  <div key={i} style={{
                    background: "#f8fafc", borderRadius: 8, padding: "10px",
                    border: "1px solid #f1f5f9",
                  }}>
                    <div style={{ fontSize: 8, color: "#94a3b8", fontWeight: 500, marginBottom: 4 }}>{m.label}</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", transition: "all 0.6s ease" }}>
                      {show ? m.after : m.before}
                    </div>
                    {show && (
                      <div style={{ display: "inline-block", background: `${m.color}15`, borderRadius: 4, padding: "1px 5px", marginTop: 3 }}>
                        <span style={{ fontSize: 8, fontWeight: 700, color: m.color }}>{m.change}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Mini chart */}
              <div style={{ marginTop: 8, background: "#f8fafc", borderRadius: 8, padding: "8px 10px", border: "1px solid #f1f5f9" }}>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 24 }}>
                  {[3, 4, 3, 5, 4, 6, 5, 7, 6, 8, 7, 10, 9, 12, show ? 18 : 8, show ? 20 : 7].map((h, i) => (
                    <div key={i} style={{
                      flex: 1, height: h, borderRadius: 2,
                      background: i >= 14 && show ? "#10B981" : (i >= 14 ? "#e5e7eb" : "#dde5ff"),
                      transition: "all 0.6s ease",
                    }}/>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ padding: "6px 16px 10px", borderTop: "1px solid #f1f1f1", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Brand/>
              <span style={{ fontSize: 8, color: "#6b7280" }}>Optimized feed = better ROAS</span>
            </div>
          </AdBase>
        );
      };
      return <Dashboard />;
    },
  },

  /* ── 17. Feed Health Monitor ── */
  {
    name: "Feed Health Monitor",
    desc: "Status indicators per feed aspect",
    render: (isW) => {
      const HealthMonitor = () => {
        const [step, setStep] = useState(0);
        useEffect(() => {
          let running = true;
          const loop = async () => {
            while (running) {
              setStep(0);
              await new Promise(r => setTimeout(r, 1000));
              for (let i = 1; i <= 6; i++) {
                if (!running) return;
                setStep(i);
                await new Promise(r => setTimeout(r, 500));
              }
              await new Promise(r => setTimeout(r, 4000));
            }
          };
          loop();
          return () => { running = false; };
        }, []);
        const checks = [
          { label: "Title length", detail: "150 chars max", before: "✗ 12 chars avg", after: "✓ 142 chars avg", icon: "📏" },
          { label: "Keyword density", detail: "SEO terms", before: "✗ 0 keywords", after: "✓ 8+ keywords", icon: "🔍" },
          { label: "Description", detail: ">500 chars", before: "✗ 120 chars", after: "✓ 4,970 chars", icon: "📄" },
          { label: "Highlights", detail: "5 required", before: "✗ 0/5", after: "✓ 5/5", icon: "⭐" },
          { label: "Image quality", detail: "Multi-angle", before: "⚠ 1 image", after: "✓ 5 images", icon: "📸" },
          { label: "GTIN / Brand", detail: "Identifiers", before: "✗ Missing", after: "✓ Verified", icon: "🏷️" },
        ];
        return (
          <AdBase bg="linear-gradient(180deg, #f8faff 0%, #fff 100%)">
            <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: isW ? "12px 14px" : "12px 14px" }}>
              <div style={{ textAlign: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 8, fontWeight: 700, color: "#4B6AE8", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 3 }}>Feed Health Monitor</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>6 critical checkpoints</div>
              </div>
              <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
                {checks.map((c, i) => {
                  const active = i < step;
                  return (
                    <div key={i} style={{
                      background: active ? "#f0fdf4" : "#fff",
                      borderRadius: 8, padding: "7px 8px",
                      border: `1px solid ${active ? "#bbf7d0" : "#e5e7eb"}`,
                      transition: "all 0.4s ease",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 3 }}>
                        <span style={{ fontSize: 10 }}>{c.icon}</span>
                        <span style={{ fontSize: 8, fontWeight: 700, color: "#0f172a" }}>{c.label}</span>
                      </div>
                      <div style={{ fontSize: 7, fontWeight: 600, color: active ? "#059669" : "#EF4444", transition: "color 0.4s ease" }}>
                        {active ? c.after : c.before}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{ padding: "6px 14px 10px", borderTop: "1px solid #f1f1f1", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Brand/>
              <CTA text="Run Health Check" small/>
            </div>
          </AdBase>
        );
      };
      return <HealthMonitor />;
    },
  },

  /* ── 18. Competition Comparison ── */
  {
    name: "vs Competitors",
    desc: "Your feed score vs competition",
    render: (isW) => (
      <AdBase bg="linear-gradient(135deg, #0f172a 0%, #1e293b 100%)">
        <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: isW ? "14px 16px" : "14px 16px" }}>
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 8, fontWeight: 700, color: "#818cf8", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 3 }}>Google Shopping</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>How does your feed compare?</div>
          </div>
          {/* Comparison bars */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { label: "Your Feed", score: 32, color: "#EF4444", highlight: true },
              { label: "Average Competitor", score: 58, color: "#F59E0B", highlight: false },
              { label: "Top 10% Sellers", score: 91, color: "#10B981", highlight: false },
              { label: "After Our Audit", score: 98, color: "#4B6AE8", highlight: true },
            ].map((item, i) => (
              <div key={i}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                  <span style={{ fontSize: 9, fontWeight: item.highlight ? 700 : 500, color: item.highlight ? "#fff" : "#94a3b8" }}>{item.label}</span>
                  <span style={{ fontSize: 10, fontWeight: 800, color: item.color }}>{item.score}/100</span>
                </div>
                <div style={{ width: "100%", height: 8, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{
                    width: `${item.score}%`, height: "100%",
                    background: i === 3 ? "linear-gradient(90deg, #4B6AE8, #818cf8)" : item.color,
                    borderRadius: 4,
                    boxShadow: item.highlight ? `0 0 8px ${item.color}40` : "none",
                  }}/>
                </div>
              </div>
            ))}
          </div>
          {/* Gap indicator */}
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <div style={{ background: "rgba(75,106,232,0.15)", borderRadius: 8, padding: "6px 14px", border: "1px solid rgba(75,106,232,0.3)" }}>
              <span style={{ fontSize: 9, color: "#c7d2fe" }}>Bridge the gap: </span>
              <span style={{ fontSize: 11, fontWeight: 800, color: "#fff" }}>32 → 98</span>
              <span style={{ fontSize: 9, color: "#818cf8" }}> in 30s</span>
            </div>
          </div>
        </div>
        <div style={{ padding: "6px 16px 10px", borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Brand light/>
          <CTA text="Compare Now" small/>
        </div>
      </AdBase>
    ),
  },
];

/* ══════════════════════════════════════════════════
   VIDEO RECORDING
   ══════════════════════════════════════════════════ */

const VIDEO_SIZES = {
  "1:1":  { w: 540, h: 540, label: "Square 1:1" },
  "16:9": { w: 720, h: 405, label: "Landscape 16:9" },
  "9:16": { w: 405, h: 720, label: "Story 9:16" },
};

const KEYFRAMES_CSS = `
  @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
  @keyframes pulseMF { 0%,100%{opacity:1} 50%{opacity:0.3} }
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
`;

function DownloadVideoButton({ ad, adIndex }) {
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [progress, setProgress] = useState(0);
  const [recFormat, setRecFormat] = useState(null);

  const startRecording = useCallback(async (fmtKey) => {
    const vf = VIDEO_SIZES[fmtKey];
    setRecording(true);
    setRecFormat(fmtKey);
    setProgress(0);
    setOpen(false);

    // Create offscreen container
    const container = document.createElement("div");
    container.style.cssText = `position:fixed;top:-9999px;left:-9999px;width:${vf.w}px;height:${vf.h}px;overflow:hidden;z-index:-1;pointer-events:none;`;
    document.body.appendChild(container);

    // Add keyframes + font
    const style = document.createElement("style");
    style.textContent = KEYFRAMES_CSS;
    container.appendChild(style);

    // Render creative
    const wrapper = document.createElement("div");
    wrapper.style.cssText = `width:${vf.w}px;height:${vf.h}px;font-family:'Inter',sans-serif;`;
    container.appendChild(wrapper);

    const isW = fmtKey === "16:9";
    const reactRoot = ReactDOM.createRoot(wrapper);
    reactRoot.render(ad.render(isW));

    // Wait for render + fonts
    await new Promise(r => setTimeout(r, 2000));

    // Setup canvas + recorder
    const canvas = document.createElement("canvas");
    canvas.width = vf.w;
    canvas.height = vf.h;
    const ctx = canvas.getContext("2d");
    const stream = canvas.captureStream(30);

    let mimeType = "video/webm;codecs=vp9";
    if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = "video/webm";

    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 5000000 });
    const chunks = [];
    recorder.ondataavailable = e => e.data.size > 0 && chunks.push(e.data);
    recorder.start(100);

    // Capture frames for 10s
    const duration = 10000;
    const start = Date.now();
    while (Date.now() - start < duration) {
      try {
        const c = await html2canvas(wrapper, {
          width: vf.w, height: vf.h, scale: 1,
          useCORS: true, logging: false,
        });
        ctx.drawImage(c, 0, 0);
      } catch (e) { /* skip frame */ }
      setProgress(Math.min(100, Math.round(((Date.now() - start) / duration) * 100)));
      await new Promise(r => setTimeout(r, 30));
    }

    recorder.stop();
    await new Promise(r => { recorder.onstop = r; });

    // Download
    const blob = new Blob(chunks, { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `MFP-${ad.name.replace(/[^a-zA-Z0-9]/g, "-")}-${fmtKey.replace(":", "x")}.webm`;
    a.click();
    URL.revokeObjectURL(url);

    // Cleanup
    reactRoot.unmount();
    document.body.removeChild(container);
    setRecording(false);
    setProgress(0);
    setRecFormat(null);
  }, [ad]);

  if (recording) {
    return (
      <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 12, height: 12, border: "2px solid #4B6AE8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, color: "#4B6AE8", fontWeight: 600, marginBottom: 3 }}>
            Recording {recFormat}... {progress}%
          </div>
          <div style={{ width: "100%", height: 3, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ width: `${progress}%`, height: "100%", background: "linear-gradient(90deg, #4B6AE8, #818cf8)", borderRadius: 2, transition: "width 0.3s ease" }}/>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 6, position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen(!open)} style={{
        background: "none", border: "1px solid #e5e7eb", borderRadius: 6,
        padding: "4px 10px", fontSize: 10, color: "#6b7280", cursor: "pointer",
        fontFamily: "'Inter',sans-serif", fontWeight: 600,
        display: "flex", alignItems: "center", gap: 4,
      }}>
        <span>🎬</span> Download Video
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, marginTop: 4, zIndex: 10,
          background: "#fff", borderRadius: 10, boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
          border: "1px solid #e5e7eb", padding: 8, minWidth: 200,
        }}>
          <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>
            Choose video format
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {Object.entries(VIDEO_SIZES).map(([key, val]) => (
              <button key={key} onClick={() => startRecording(key)} style={{
                background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 8,
                padding: "8px 12px", fontSize: 11, color: "#374151", cursor: "pointer",
                fontFamily: "'Inter',sans-serif", fontWeight: 600, textAlign: "left",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <span>{val.label}</span>
                <span style={{ fontSize: 9, color: "#94a3b8" }}>{val.w}×{val.h}</span>
              </button>
            ))}
          </div>
          <div style={{ fontSize: 8, color: "#94a3b8", marginTop: 6, lineHeight: 1.4 }}>
            Records 10s of animation as .webm<br/>
            Compatible with Facebook, YouTube, Google Ads
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════ */

const FORMATS = [
  { key: "1:1", label: "Square", sub: "1200×1200", w: 380, ratio: "1/1" },
  { key: "16:9", label: "Landscape", sub: "1920×1080", w: 560, ratio: "16/9" },
  { key: "4:5", label: "Portrait", sub: "1080×1350", w: 320, ratio: "4/5" },
  { key: "9:16", label: "Story", sub: "1080×1920", w: 240, ratio: "9/16" },
];

export default function AdsCreatives() {
  const [activeFormat, setActiveFormat] = useState("1:1");
  const fmt = FORMATS.find(f => f.key === activeFormat);
  const isW = activeFormat === "16:9";

  return (
    <div>
      {/* Format tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 28, flexWrap: "wrap" }}>
        {FORMATS.map(f => (
          <button key={f.key} onClick={() => setActiveFormat(f.key)} style={{
            padding: "10px 20px", borderRadius: 10, cursor: "pointer",
            background: activeFormat === f.key ? "linear-gradient(135deg, #4B6AE8, #6B8BF5)" : "#fff",
            color: activeFormat === f.key ? "#fff" : "#6b7280",
            border: activeFormat === f.key ? "none" : "1.5px solid #e5e7eb",
            fontSize: 13, fontWeight: 600, fontFamily: "'Inter',sans-serif",
            boxShadow: activeFormat === f.key ? "0 4px 16px rgba(75,106,232,0.3)" : "0 1px 3px rgba(0,0,0,0.04)",
            transition: "all 0.2s ease",
          }}>
            <div>{f.label} {f.key}</div>
            <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>{f.sub}</div>
          </button>
        ))}
      </div>

      {/* Creative grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isW ? "1fr" : (activeFormat === "9:16" ? "repeat(auto-fill, minmax(200px, 1fr))" : "repeat(auto-fill, minmax(300px, 1fr))"),
        gap: 24,
        justifyItems: isW ? "center" : "stretch",
      }}>
        {ADS.map((ad, i) => (
          <div key={`${activeFormat}-${i}`}>
            <div style={{
              width: isW ? fmt.w : "100%",
              maxWidth: fmt.w,
              aspectRatio: fmt.ratio,
              borderRadius: 14,
              overflow: "hidden",
              boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}>
              {ad.render(isW)}
            </div>
            <div style={{ marginTop: 10, display: "flex", alignItems: "baseline", gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#4B6AE8" }}>{String(i + 1).padStart(2, "0")}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{ad.name}</div>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>{ad.desc}</div>
              </div>
            </div>
            <DownloadVideoButton ad={ad} adIndex={i} />
          </div>
        ))}
      </div>
    </div>
  );
}
